import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Banknote,
  ArrowLeft,
  Loader2,
  ShieldAlert,
  ExternalLink,
  Info,
  Landmark,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import airportImage from '../../assets/airportt.png';
import useWalletData from '../../hooks/useWalletData';
import firebaseFunctionsService from '../../services/firebaseFunctionsService';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { db, auth } from '../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const quickAmounts = [25, 50, 100, 200, 500];
const STRIPE_SUPPORTED_COUNTRIES = ['CA'];

const formatCurrency = value => {
  if (!value) return '';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '';
  const formatted = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(numValue));
  const sign = numValue < 0 ? '-' : '';
  return `${sign}CAD ${formatted}`;
};

export default function WithdrawMoneyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { balance, accountDoc } = useWalletData(!!user);
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasStripeAccount, setHasStripeAccount] = useState(false);
  const [accountReady, setAccountReady] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [accountDetails, setAccountDetails] = useState(null);
  const [isSettingUpAccount, setIsSettingUpAccount] = useState(false);
  const [isOnboardingPanelOpen, setIsOnboardingPanelOpen] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState('');
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState(null);
  const [stripeConnectAccountId, setStripeConnectAccountId] = useState(null);

  // Check account status on mount and when accountDoc changes
  useEffect(() => {
    const refreshAccountStatus = async () => {
      if (!user?.uid) return;

      setCheckingAccount(true);
      try {
        // First check Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const connectAccountId = userData?.stripeConnectAccountId;

          if (connectAccountId) {
            console.log('✅ Stripe Connect account found:', connectAccountId);
            setStripeConnectAccountId(connectAccountId);
            setHasStripeAccount(true);

            // Check account status from Stripe
            try {
              const authToken = await auth.currentUser?.getIdToken();
              const statusResult =
                await firebaseFunctionsService.checkAccountStatus(
                  user.uid,
                  authToken,
                );
              if (statusResult.success) {
                const isReady = statusResult.accountReady || false;
                setAccountReady(isReady);
                setAccountDetails(statusResult.accountDetails || null);

                console.log('📊 Account status from Stripe:', {
                  accountReady: isReady,
                  accountDetails: statusResult.accountDetails,
                  chargesEnabled: statusResult.accountDetails?.chargesEnabled,
                  payoutsEnabled: statusResult.accountDetails?.payoutsEnabled,
                  detailsSubmitted:
                    statusResult.accountDetails?.detailsSubmitted,
                });
              } else {
                setAccountReady(false);
              }
            } catch (statusError) {
              console.error(
                '❌ Error checking account status from Stripe:',
                statusError,
              );
              setAccountReady(false);
            }
          } else {
            console.log('⚠️ Stripe Connect account not found');
            setHasStripeAccount(false);
            setAccountReady(false);
            setStripeConnectAccountId(null);
          }
        }
      } catch (error) {
        console.error('❌ Error checking account status:', error);
        setAccountReady(false);
      } finally {
        setCheckingAccount(false);
      }
    };

    if (user?.uid) {
      refreshAccountStatus();
    }
  }, [user?.uid, accountDoc?.stripeConnectAccountId]);

  const handleQuickAmount = value => {
    if (value <= balance) {
      setAmount(value.toString());
    } else {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.insufficientBalance', {
          available: formatCurrency(balance),
        }),
      });
    }
  };

  const handleCheckStatus = async () => {
    if (!user?.uid) return;
    setCheckingAccount(true);
    try {
      const authToken = await auth.currentUser?.getIdToken();
      const statusResult = await firebaseFunctionsService.checkAccountStatus(
        user.uid,
        authToken,
      );
      if (statusResult.success) {
        if (statusResult.hasStripeAccount) {
          setHasStripeAccount(true);
          setStripeConnectAccountId(statusResult.stripeConnectAccountId);
          setAccountReady(statusResult.accountReady || false);
          setAccountDetails(statusResult.accountDetails || null);

          if (statusResult.accountReady) {
            setStatus({
              type: 'success',
              message: t('wallet.withdrawMoney.accountReady'),
            });
          } else {
            setStatus({
              type: 'error',
              message: t('wallet.accountSetupInProgress'),
            });
          }
        } else {
          setHasStripeAccount(false);
          setAccountReady(false);
          setStripeConnectAccountId(null);
          setStatus({
            type: 'error',
            message: t('wallet.withdrawMoney.noAccountFound'),
          });
        }
      }
    } catch (error) {
      console.error('❌ Error checking account status:', error);
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.failedToCheckStatus', {
          error: error.message,
        }),
      });
    } finally {
      setCheckingAccount(false);
    }
  };

  const handleSetupStripeAccount = async () => {
    if (!user?.uid || !user?.email) return;

    // Check if user's country is supported for Stripe Connect payouts
    let userCountry = user?.country || user?.countryCode || null;

    // Fallback to Firestore only if not in user object
    if (!userCountry) {
      try {
        const userDoc = await Promise.race([
          getDoc(doc(db, 'users', user.uid)),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 3000),
          ),
        ]);
        const userData = userDoc.data();
        userCountry = userData?.country || userData?.countryCode || null;
      } catch (fetchError) {
        console.warn('Failed to fetch country from Firestore:', fetchError);
        userCountry = 'CA';
      }
    }

    // If country is not set, default to 'CA' (Canada) and save to Firestore
    if (!userCountry) {
      console.log('⚠️ Country not found, setting default to CA');
      userCountry = 'CA';
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          {
            country: 'CA',
            countryCode: 'CA',
          },
          { merge: true },
        );
        console.log('✅ Default country CA set in Firestore');
      } catch (updateError) {
        console.warn('Failed to set default country:', updateError);
      }
    }

    if (!STRIPE_SUPPORTED_COUNTRIES.includes(userCountry.toUpperCase())) {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.payoutsNotAvailable', {
          country: userCountry,
        }),
      });
      return;
    }

    setIsSettingUpAccount(true);
    setOnboardingLoading(true);
    setOnboardingError(null);
    try {
      let connectId =
        accountDoc?.stripeConnectAccountId || stripeConnectAccountId;
      if (!connectId) {
        const accountRes = await firebaseFunctionsService.createConnectAccount(
          user.email,
          user.uid,
        );
        connectId = accountRes.accountId || connectId;
      }
      const linkRes = await firebaseFunctionsService.createAccountLink(
        connectId,
      );
      if (!linkRes?.url) {
        throw new Error(t('wallet.withdrawMoney.unableToGetOnboardingLink'));
      }
      setOnboardingUrl(linkRes.url);
      setIsOnboardingPanelOpen(true);
      window.open(linkRes.url, '_blank', 'noopener');
    } catch (error) {
      setOnboardingError(error.message || t('wallet.withdrawalAccount.unableToStartOnboarding'));
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.failedToSetupAccount', {
          error: error.message,
        }),
      });
    } finally {
      setOnboardingLoading(false);
      setIsSettingUpAccount(false);
    }
  };

  const handleWithdraw = async event => {
    event.preventDefault();
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.enterValidAmount'),
      });
      return;
    }

    if (numericAmount > balance) {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.insufficientBalance', {
          available: formatCurrency(balance),
        }),
      });
      return;
    }

    if (!stripeConnectAccountId && !hasStripeAccount) {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.connectAccountFirst'),
      });
      return;
    }

    // Check if user's country is supported for Stripe payouts
    let userCountry = user?.country || user?.countryCode || null;

    // Fallback to Firestore only if not in user object
    if (!userCountry) {
      try {
        const userDoc = await Promise.race([
          getDoc(doc(db, 'users', user.uid)),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 3000),
          ),
        ]);
        const userData = userDoc.data();
        userCountry = userData?.country || userData?.countryCode || null;
      } catch (fetchError) {
        console.warn('Failed to fetch country from Firestore:', fetchError);
        userCountry = 'CA';
      }
    }

    // If country is not set, default to 'CA' (Canada) and save to Firestore
    if (!userCountry) {
      console.log('⚠️ Country not found, setting default to CA');
      userCountry = 'CA';
      try {
        await setDoc(
          doc(db, 'users', user.uid),
          {
            country: 'CA',
            countryCode: 'CA',
          },
          { merge: true },
        );
        console.log('✅ Default country CA set in Firestore');
      } catch (updateError) {
        console.warn('Failed to set default country:', updateError);
      }
    }

    if (!STRIPE_SUPPORTED_COUNTRIES.includes(userCountry.toUpperCase())) {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.withdrawalsNotAvailable', {
          country: userCountry,
        }),
      });
      return;
    }

    // Check account status before proceeding
    if (!stripeConnectAccountId) {
      setStatus({
        type: 'error',
        message: t('wallet.withdrawMoney.connectAccountFirst'),
      });
      return;
    }

    // If account exists but not ready, check status from Stripe
    if (!accountReady) {
      console.log('⚠️ Account not ready, checking status from Stripe...');
      try {
        const authToken = await auth.currentUser?.getIdToken();
        const statusResult = await firebaseFunctionsService.checkAccountStatus(
          user.uid,
          authToken,
        );
        if (statusResult.success && statusResult.accountReady) {
          setAccountReady(true);
          setAccountDetails(statusResult.accountDetails || null);
          console.log('✅ Account is ready for transfers');
        } else {
          setStatus({
            type: 'error',
            message: t('wallet.withdrawMoney.accountNeedsSetup'),
          });
          return;
        }
      } catch (statusError) {
        console.error('❌ Error checking account status:', statusError);
        setStatus({
          type: 'error',
          message: t('wallet.withdrawMoney.accountSetupInProgress'),
        });
        return;
      }
    }

    setIsProcessing(true);
    setStatus(null);
    try {
      const accountId =
        stripeConnectAccountId || accountDoc?.stripeConnectAccountId;
      const authToken = await auth.currentUser?.getIdToken();
      await firebaseFunctionsService.createTransfer(
        numericAmount,
        accountId,
        user.uid,
        authToken,
      );
      setStatus({
        type: 'success',
        message: t('wallet.withdrawMoney.withdrawalQueued', {
          amount: formatCurrency(numericAmount),
        }),
      });
      setAmount('');
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || t('wallet.withdrawMoney.failedToSubmit'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
              {t('wallet.withdrawMoney.title')}
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('wallet.withdrawMoney.signInToAccess')}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black dark:bg-slate-700 px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('common.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[420px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/wallet')}
            className="inline-flex items-center gap-2 text-white/80 text-sm font-semibold mb-6 hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('wallet.allTransactions.backToWallet')}
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.4em] text-yellow-200">
                {t('wallet.withdrawFunds')}
              </p>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('wallet.cashOutEarnings')}
              </h1>
              <p className="text-xl text-yellow-50 mb-6 max-w-2xl">
                {t('wallet.transferBalanceDescription')}
              </p>
              <div className="rounded-2xl bg-white/10 border border-white/20 p-4 text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-yellow-200 mb-1">
                  {t('wallet.availableBalance')}
                </p>
                <p className="text-3xl font-black">{formatCurrency(balance)}</p>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img
                src={airportImage}
                alt="Withdraw Money"
                className="w-full max-w-md h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        {/* Setup Card - Show if no account */}
        {!hasStripeAccount && (
          <div className="rounded-3xl bg-white dark:bg-slate-900 shadow-xl border border-gray-100 dark:border-slate-800 p-8 space-y-6 mb-8">
            <div className="flex items-center gap-3">
              <Landmark className="h-6 w-6 text-yellow-500 dark:text-yellow-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('wallet.setupWithdrawalAccount')}
              </h2>
            </div>
            <p className="text-gray-600 dark:text-slate-400">
              {t('wallet.connectStripeDescription')}
            </p>
            <button
              type="button"
              onClick={handleSetupStripeAccount}
              disabled={isSettingUpAccount}
              className="w-full rounded-2xl bg-yellow-500 dark:bg-yellow-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-yellow-600 dark:hover:bg-yellow-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSettingUpAccount ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('wallet.settingUp')}
                </>
              ) : (
                t('wallet.setupAccount')
              )}
            </button>
            <button
              type="button"
              onClick={handleCheckStatus}
              disabled={checkingAccount}
              className="w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3 text-base font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center justify-center gap-2"
            >
              {checkingAccount ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('wallet.checking')}
                </>
              ) : (
                t('wallet.checkStatus')
              )}
            </button>
          </div>
        )}

        {/* Withdrawal Form - Show if account exists */}
        {hasStripeAccount && (
          <form
            onSubmit={handleWithdraw}
            className="rounded-3xl bg-white dark:bg-slate-900 shadow-xl border border-gray-100 dark:border-slate-800 p-8 space-y-8"
          >
            {/* Account Status */}
            {checkingAccount ? (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-slate-800">
                <Loader2 className="h-5 w-5 animate-spin text-gray-500 dark:text-slate-400" />
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  {t('wallet.checkingAccountStatus')}
                </p>
              </div>
            ) : accountReady ? (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/50">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                  {t('wallet.accountReadyForWithdrawals')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">
                    {t('wallet.accountSetupInProgress')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSetupStripeAccount}
                  disabled={isSettingUpAccount}
                  className="w-full rounded-2xl bg-yellow-500 dark:bg-yellow-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-yellow-600 dark:hover:bg-yellow-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSettingUpAccount ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('wallet.settingUp')}
                    </>
                  ) : (
                    t('wallet.completeAccountSetup')
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCheckStatus}
                  disabled={checkingAccount}
                  className="w-full rounded-2xl border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 px-6 py-3 text-base font-semibold text-green-700 dark:text-green-400 transition hover:bg-green-100 dark:hover:bg-green-900/30 flex items-center justify-center gap-2"
                >
                  {checkingAccount ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('wallet.checking')}
                    </>
                  ) : (
                    t('wallet.refreshAccountStatus')
                  )}
                </button>
              </div>
            )}

            {/* Account Information Card */}
            {accountReady && hasStripeAccount && stripeConnectAccountId && (
              <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <Landmark className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('wallet.withdrawalAccount')}
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-slate-400">
                      {t('wallet.accountId')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {stripeConnectAccountId.substring(0, 12)}...
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-slate-400">
                      {t('wallet.payouts')}:
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        accountDetails?.payoutsEnabled === true
                          ? 'text-green-600 dark:text-green-400'
                          : accountDetails?.payoutsEnabled === false
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-yellow-600 dark:text-yellow-400'
                      }`}
                    >
                      {accountDetails?.payoutsEnabled === true
                        ? t('wallet.enabled')
                        : accountDetails?.payoutsEnabled === false
                        ? t('wallet.disabled')
                        : t('wallet.pending')}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSetupStripeAccount}
                  className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  {t('wallet.changeWithdrawalAccount')}
                </button>
              </div>
            )}

            <div>
              <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest mb-3">
                {t('wallet.withdrawalAmount')}
              </p>

              <div className="flex items-center gap-3 rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-4 focus-within:ring-2 focus-within:ring-yellow-300 dark:focus-within:ring-yellow-500 focus-within:bg-white dark:focus-within:bg-slate-700">
                <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                  CAD
                </span>
                <input
                  type="number"
                  min="1"
                  placeholder="0.00"
                  value={amount}
                  onChange={event => setAmount(event.target.value)}
                  className="flex-1 bg-transparent text-2xl font-semibold text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none"
                />
              </div>

              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest mb-3">
                  {t('wallet.quickAmounts')}
                </p>
                <div className="flex flex-wrap gap-3">
                  {quickAmounts.map(value => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleQuickAmount(value)}
                      disabled={value > balance}
                      className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                        Number(amount) === value
                          ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
                          : value > balance
                          ? 'border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900 text-gray-400 dark:text-slate-600 cursor-not-allowed'
                          : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:border-yellow-300 dark:hover:border-yellow-500'
                      }`}
                    >
                      {formatCurrency(value)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {status && (
              <div
                className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                  status.type === 'error'
                    ? 'border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'
                    : 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                }`}
              >
                {status.message}
              </div>
            )}

            <button
              type="submit"
              disabled={
                isProcessing ||
                !amount ||
                Number(amount) <= 0 ||
                Number(amount) > balance ||
                !accountReady
              }
              className="w-full rounded-2xl bg-red-600 dark:bg-red-700 px-6 py-4 text-base font-semibold text-white transition hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isProcessing
                ? t('wallet.submitting')
                : t('wallet.withdraw', {
                    amount: formatCurrency(Number(amount) || 0),
                  })}
            </button>

            <div className="flex items-start gap-3 rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4 text-sm text-gray-700 dark:text-slate-300">
              <ShieldAlert className="h-5 w-5 text-gray-500 dark:text-slate-400" />
              <p>
                {t('wallet.withdrawalsProcessed')}
              </p>
            </div>

            <div className="rounded-2xl border border-dashed border-gray-300 dark:border-slate-700 p-4 text-sm text-gray-600 dark:text-slate-400 space-y-2">
              <div className="flex items-center gap-2 font-semibold text-gray-800 dark:text-slate-200">
                <Info className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
                {t('wallet.stripeTestInstructions')}
              </div>
              <p>
                {t('wallet.connectUpdateInstructions')}
              </p>
              <p>
                {t('wallet.bankingInfoInstructions')}
              </p>
              <p>
                {t('wallet.afterSubmittingInstructions')}
              </p>
            </div>
          </form>
        )}
      </main>

      <div
        className={`fixed inset-0 z-50 transition ${
          isOnboardingPanelOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            isOnboardingPanelOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsOnboardingPanelOpen(false)}
        ></div>
        <div
          className={`absolute top-0 right-0 h-full w-full sm:w-[520px] bg-white dark:bg-slate-900 shadow-2xl transition-transform duration-300 flex flex-col ${
            isOnboardingPanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-gray-400 dark:text-slate-500">
                Stripe
              </p>
              <h3 className="text-lg font-black text-gray-900 dark:text-white">
                {t('wallet.withdrawMoney.connectPayoutAccount')}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setIsOnboardingPanelOpen(false)}
              className="text-sm font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
            >
              {t('wallet.close')}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {onboardingLoading ? (
              <div className="flex items-center justify-center h-40 text-gray-500 dark:text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                {t('wallet.withdrawMoney.generatingOnboardingLink')}
              </div>
            ) : onboardingUrl ? (
              <>
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  {t('wallet.withdrawMoney.onboardingOpened')}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    window.open(onboardingUrl, '_blank', 'noopener')
                  }
                  className="w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white transition hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center justify-center gap-2"
                >
                  {t('wallet.withdrawMoney.openOnboarding')}
                  <ExternalLink className="h-4 w-4" />
                </button>
              </>
            ) : onboardingError ? (
              <p className="text-sm text-rose-500 dark:text-rose-400 font-semibold">
                {onboardingError}
              </p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-slate-400">
                {t('wallet.withdrawMoney.clickToLaunch')}
              </p>
            )}
          </div>
          <div className="p-6 border-t border-gray-100 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setIsOnboardingPanelOpen(false)}
              className="w-full rounded-2xl bg-black dark:bg-slate-700 text-yellow-400 font-semibold py-3 hover:bg-gray-900 dark:hover:bg-slate-800 transition"
            >
              {t('wallet.done')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
